import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChatBot } from '../../components/ChatBot';
import { DeleteButton } from '../../components/DeleteButton';
import { SimpleChatClient } from './SimpleChatClient';
function toDisplayText(msg) {
    if (typeof msg.message === 'string')
        return msg.message;
    return JSON.stringify(msg.message);
}
function toChatMessages(history) {
    return history.map((msg) => ({
        role: msg.role,
        content: toDisplayText(msg),
        messageType: msg.messageType,
    }));
}
export const FluidChatBot = ({ conversationId: initialConversationId, chatPanelTitle, signature, rounded = true, hideClearButton = false, }) => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState(initialConversationId);
    const clientRef = useRef(new SimpleChatClient(signature));
    const startedRef = useRef(false);
    const startConversation = useCallback(async (resumeId) => {
        setIsLoading(true);
        try {
            const result = await clientRef.current.startConversation(resumeId);
            setConversationId(result.conversationId);
            setMessages(toChatMessages(result.messageHistory));
        }
        catch (error) {
            console.error('Error starting conversation:', error);
            setMessages([{
                    role: 'assistant',
                    content: 'Sorry, I had trouble starting the conversation. Please try again.',
                    messageType: 'error',
                }]);
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        if (startedRef.current)
            return;
        startedRef.current = true;
        startConversation(initialConversationId);
    }, [initialConversationId, startConversation]);
    const handleMessage = useCallback(async (chatMessage) => {
        if (!conversationId)
            return;
        setMessages((prev) => [...prev, chatMessage]);
        setIsLoading(true);
        try {
            const result = await clientRef.current.sendMessage(conversationId, chatMessage.content);
            setMessages(toChatMessages(result.messageHistory));
        }
        catch (error) {
            console.error('Error sending message:', error);
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: 'Sorry, I encountered an error. Please try again.',
                    messageType: 'error',
                },
            ]);
        }
        finally {
            setIsLoading(false);
        }
    }, [conversationId]);
    const handleClear = useCallback(async () => {
        if (conversationId) {
            try {
                await clientRef.current.deleteConversation(conversationId);
            }
            catch (error) {
                console.error('Error clearing conversation:', error);
            }
        }
        setMessages([]);
        setConversationId(undefined);
        startedRef.current = false;
        startConversation();
    }, [conversationId, startConversation]);
    return (React.createElement(ChatBot, { chatPanelTitle: chatPanelTitle, messages: messages, onMessage: handleMessage, isLoading: isLoading, rounded: rounded, footer: !hideClearButton ? (React.createElement("div", { className: "flex items-center justify-end" },
            React.createElement(DeleteButton, { iconOnly: true, title: "Clear conversation", message: "Are you sure you want to clear this conversation? This will delete the conversation history and start a new one.", onConfirm: handleClear }))) : undefined }));
};
